import { Tooltip } from 'antd'
import React, { useState, useRef, useEffect } from 'react'
import { LoadingOutlined } from '@ant-design/icons';
import { MdLocalAirport, MdPhone, MdLocationCity, MdPlace, MdClose } from 'react-icons/md';
import { FaShuttleVan, FaHandshake } from 'react-icons/fa';
import axiosInstance from '../../Config/api';
import API_ENDPOINTS from '../../Config/api/endpoints';
import {
    getLocationPickerIconKind,
    getLocationPickerIconKindFromType,
    parseSearchAddressEntry,
    splitAddressForDropdownLines,
} from '../../Config/utils/addressDisplayUtils';

const ICON_BY_KIND = {
    airport: MdLocalAirport,
    phone: MdPhone,
    city: MdLocationCity,
    shuttle: FaShuttleVan,
    meet: FaHandshake,
    default: MdPlace,
};

function escapeRegExp(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Wraps case-insensitive query matches in a neutral highlight (not yellow).
 */
function renderWithQueryHighlight(text, query) {
    const t = text != null ? String(text) : '';
    const q = (query || '').trim();
    if (!q || !t) return t;

    const parts = t.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
    return parts.map((part, index) => {
        if (part.toLowerCase() === q.toLowerCase()) {
            return (
                <mark
                    key={index}
                    className="bg-neutral-200 text-inherit rounded px-0.5 py-0 [box-decoration-break:clone]"
                >
                    {part}
                </mark>
            );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
}

function AddressDropdownText({ address, airportIataCode, query }) {
    const layout = splitAddressForDropdownLines(address);

    if (layout.type === 'paren') {
        const { beforeOpen, parenBlock, afterClose } = layout;
        return (
            <div className="min-w-0 flex-1 leading-snug">
                <div className="text-sm font-semibold text-[#1a1a1a] break-words whitespace-normal">
                    <span>{renderWithQueryHighlight(beforeOpen, query)}</span>
                    {renderWithQueryHighlight(parenBlock, query)}
                </div>
                {(afterClose || airportIataCode) ? (
                    <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="break-words whitespace-normal">{renderWithQueryHighlight(afterClose, query)}</span>
                        {airportIataCode ? <span className="font-semibold text-[#1a1a1a] whitespace-nowrap">{airportIataCode}</span> : null}
                    </div>
                ) : null}
            </div>
        );
    }

    const { line1, line2 } = layout;
    return (
        <div className="min-w-0 flex-1 leading-snug">
            <div className="text-sm font-semibold text-[#1a1a1a] break-words whitespace-normal">{renderWithQueryHighlight(line1, query)}</div>
            {(line2 || airportIataCode) ? (
                <div className="text-xs text-neutral-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="break-words whitespace-normal">{renderWithQueryHighlight(line2, query)}</span>
                    {airportIataCode ? <span className="font-semibold text-[#1a1a1a] whitespace-nowrap">{airportIataCode}</span> : null}
                </div>
            ) : null}
        </div>
    );
}

const LocationPicker = ({ id, placeholder, inputClasses, searchContainerClasses, searchContainerStyle, value, onChange }) => {

    const [searchValue, setSearchValue] = useState(value || '');
    const [spin, setSpin] = useState(false);
    const [searchResults, setsearchResults] = useState([]);
    const debounceRef = useRef(null);

    const fetchAddresses = async (query) => {
        // Only search when at least 3 characters are entered
        if (!query || query.trim().length < 3) {
            setsearchResults([]);
            setSpin(false);
            return;
        }

        try {
            setSpin(true);
            const response = await axiosInstance.get(API_ENDPOINTS.VEHICLE.SEARCH_ADDRESS, {
                params: { key: query.trim() },
            });

            const data = response?.data?.data || [];
            // Limit to 20 addresses
            setsearchResults(Array.isArray(data) ? data.slice(0, 20) : []);
        } catch (error) {
            console.error('Failed to search addresses:', error);
            setsearchResults([]);
        } finally {
            setSpin(false);
        }
    };

    const debounceSearch = (query, delay) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            fetchAddresses(query);
        }, delay);
    };

    const inputChangeValueHandler = (e) => {
        const query = e.target.value;
        setSearchValue(query);
        if (onChange) {
            onChange(query);
        }
        debounceSearch(query, 500);
    }

    // Update local state when value prop changes
    useEffect(() => {
        if (value !== undefined) {
            setSearchValue(value);
        }
    }, [value]);

    const pickedLocation = (address) => {
        setSearchValue(address);
        if (onChange) {
            onChange(address);
        }
        setsearchResults([]);
    }

    const clearLocation = () => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        setSearchValue('');
        if (onChange) {
            onChange('');
        }
        setsearchResults([]);
        setSpin(false);
    };

    const showClear = searchValue.trim().length > 0;
    const inputRightPad =
        showClear && spin ? 'pr-[4.25rem]' : showClear || spin ? 'pr-10' : 'pr-2';

    return (
        <div className="relative w-full">
            <input
                id={id}
                type='text'
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                className={`p-2 outline-none w-full truncate ${inputRightPad} ${inputClasses ? inputClasses : ''}`}
                placeholder={placeholder ? placeholder : 'Select your city'}
                value={searchValue}
                onChange={inputChangeValueHandler}
            />
            <div
                className="pointer-events-none absolute right-2 top-1/2 z-[2] flex -translate-y-1/2 items-center gap-0.5"
                aria-hidden={!showClear && !spin}
            >
                {spin ? (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center text-[#13253F]">
                        <LoadingOutlined spin className="text-lg" />
                    </span>
                ) : null}
                {showClear ? (
                    <button
                        type="button"
                        aria-label="Clear location"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            clearLocation();
                        }}
                        className="pointer-events-auto flex h-6 w-6 shrink-0 items-center justify-center rounded text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1"
                    >
                        <MdClose size={15} aria-hidden />
                    </button>
                ) : null}
            </div>
            {
                searchResults && searchResults.length > 0 &&
                <div
                    className={`absolute left-0 right-0 top-full z-50 mt-1 max-h-[min(320px,55vh)] overflow-y-auto bg-white shadow-lg border border-neutral-200 ${searchContainerClasses ? searchContainerClasses : ""}`}
                    style={searchContainerStyle}
                >
                    {searchResults.map((rawAddress, index) => {
                        const parsed = parseSearchAddressEntry(rawAddress);
                        const kindByType = getLocationPickerIconKindFromType(parsed.rawType);
                        const kind = kindByType || getLocationPickerIconKind(rawAddress);
                        const Icon = ICON_BY_KIND[kind] || ICON_BY_KIND.city;
                        const addressToShow = parsed.address || rawAddress;
                        const tooltipTitle = parsed.type
                            ? `${parsed.type}: ${addressToShow}${parsed.airportIataCode ? ` (${parsed.airportIataCode})` : ''}`
                            : `${addressToShow}${parsed.airportIataCode ? ` (${parsed.airportIataCode})` : ''}`;
                        return (
                            <Tooltip key={index} title={tooltipTitle}>
                                <button
                                    type="button"
                                    className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-neutral-100 border-b border-neutral-100 last:border-b-0"
                                    onClick={() => pickedLocation(addressToShow)}
                                >
                                    <Icon className="mt-0.5 shrink-0 text-[#7BBCB0]" size={18} aria-hidden />
                                    <AddressDropdownText
                                        address={addressToShow}
                                        airportIataCode={parsed.airportIataCode}
                                        query={searchValue}
                                    />
                                </button>
                            </Tooltip>
                        );
                    })}
                </div>
            }
        </div>
    )
}

export default LocationPicker
